import React, { useState, useEffect } from "react";
import logo from "../img/enjuggernaut_logo.png";
import { Contact } from "../Contact/Contact";
import { Experience } from "../Experience/Experience";
import { Projects } from "../Projects/Projects";
import { Education } from "../Education/Education";
import "./Select.css";
import arrow_back from "../img/arrow_back_gray.svg";
import { Finish } from "../Finish/Finish";
import { useSelector, useDispatch } from "react-redux";
import { AppState } from "../store";
import { UserType, UserScore, User } from "../Profile";
import { useNavigate } from "react-router-dom";
import close from "../img/close.svg";
import { TemplatesDisplay } from "../Templates/TemplatesDisplay";
import { useAuthToken } from "../AuthProvider";
import {
  Box,
  Button,
  Step,
  StepLabel,
  Stepper,
  Typography,
} from "@mui/material";
import { CustomStepIcon, CustomStepIconProps } from "../CustomStepIcon";
import { StepIconProps } from "@mui/material";
import { Sidebar } from "../Sidebar";
import { toast } from "react-toastify";

const steps = ["Basic Info", "Experience", "Projects", "Education", "Finish"];

export function Select() {
  const [last, setLast] = useState(false);
  const dispatch = useDispatch();
  const [selectedComponent, setSelectedComponent] = useState<JSX.Element>(
    <Contact handleClick={() => setActiveStep(activeStep + 1)} />
  );
  const [resultsSubmitted, setResultsSubmitted] = useState(false);
  const { isAuthenticated, token, isLoading } = useAuthToken();
  const navigate = useNavigate();
  const userInfo = useSelector((state: AppState) => state.user); // get the current user object from the Redux store
  const [activeStep, setActiveStep] = useState(0);
  const [completedSteps, setCompletedSteps] = useState<number[]>([]);
  const [isTemplatesOverlayOpen, setIsTemplatesOverlayOpen] =
    React.useState<boolean>(false);

  useEffect(() => {
    if (!isLoading && !resultsSubmitted) {
      checkRequireSignin();
    }
  }, [isLoading, resultsSubmitted]);

  const updateUserResume = (uuid: string) => {
    const updatedUser: User = {
      ...userInfo, // create a new object with all existing properties of the user object
      resume_uuid: uuid,
    };
    dispatch({
      type: "UPDATE_STATE",
      payload: updatedUser,
    });
  };

  async function postData() {
    if (userInfo.name.length < 1) {
      toast.error("Name cannot be empty!");
      return;
    }

    if (userInfo.email.length < 1) {
      toast.error("Email cannot be empty!");
      return;
    }

    const regexp = new RegExp(
      /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
    );

    if (!regexp.test(userInfo.email)) {
      toast.error("Email invalid!");
      return;
    }

    if (
      userInfo.experiences.length +
        userInfo.educations.length +
        userInfo.projects.length <
      2
    ) {
      toast.error(
        "Please enter at least 2 primary items (education, experience, projects)."
      );
      return;
    }

    setResultsSubmitted(true);

    const apiEndpoint =  "/api/resume";

    const headers: [string, string][] = [];

    if (isAuthenticated) {
      headers.push(["Authorization", `Bearer ${token}`]);
    }

    headers.push(["Content-Type", "application/json"]);

    try {
      // Make a POST request using fetch
      const response = await fetch(apiEndpoint, {
        method: "POST",
        headers: headers,
        body: JSON.stringify(userInfo),
      });

      if (response.ok) {
        const responseData = await response.json();

        //if (isAuthenticated) {
        const uuid = responseData.uuid as string;
        updateUserResume(uuid);
        navigate("/score");
        // } else {
        //   const scores = responseData.message as UserScore;
        //   const uuid = responseData.uuid as string;
        //   updateUserScore(scores, uuid);
        //   navigate("/score");
        // }
      } else if (response.status == 400) {
        toast.error(
          "Input information was too large! Please reduce the amount of information provided."
        );
      } else if (response.status == 405) {
        toast.error("Please sign in to continue using enjuggernaut.");
      } else if (response.status == 406) {
        toast.error("Insufficient resume credits.");
      } else {
        console.error("Error:", response.statusText);
      }
    } catch (error) {
      console.error("Error:", error);
      toast.error("An error occured.");
    }
  }

  async function checkRequireSignin() {
    const apiEndpoint =  "/api/requiresignin";

    const headers: [string, string][] = [];

    if (isAuthenticated) {
      headers.push(["Authorization", `Bearer ${token}`]);
    }

    headers.push(["Content-Type", "application/json"]);

    try {
      const response = await fetch(apiEndpoint, {
        method: "GET",
        headers: headers,
      });

      const responseData = await response.json();

      if (responseData.required) {
        navigate("/signup");
      }
    } catch (error) {
      console.error("Error:", error);
      toast.error("An error occured.");
    }
  }

  const handleComponentSelect = (component: number) => {
    setLast(false);
    switch (component) {
      case 0:
        setSelectedComponent(
          <Contact
            handleClick={() => {
              setActiveStep(activeStep + 1);
              setCompletedSteps(completedSteps.concat(0));
            }}
          />
        );
        break;
      case 1:
        setSelectedComponent(
          <Experience
            handleClick={() => {
              setActiveStep(activeStep + 1);
              setCompletedSteps(completedSteps.concat(1));
            }}
          />
        );
        break;
      case 2:
        setSelectedComponent(
          <Projects
            handleClick={() => {
              setActiveStep(activeStep + 1);
              setCompletedSteps(completedSteps.concat(2));
            }}
          />
        );
        break;
      case 3:
        setSelectedComponent(
          <Education
            handleClick={() => {
              setActiveStep(activeStep + 1);
              setCompletedSteps(completedSteps.concat(3));
            }}
          />
        );
        break;
      case 4:
        setLast(true);
        setSelectedComponent(
          <Finish handleClick={() => setIsTemplatesOverlayOpen(true)} />
        );
        break;
    }
  };

  useEffect(() => {
    handleComponentSelect(activeStep);
  }, [activeStep]);

  return (
    <div className="dark-panel flex min-h-screen flex-row">
      <div className="w-full">
        {isTemplatesOverlayOpen && (
          <div className="overlay z-10">
            <div className="questionnaire-shadow flex w-4/5 flex-col rounded-md bg-white py-[20px]">
              <button
                className="ml-auto mr-6 mt-4"
                onClick={() => setIsTemplatesOverlayOpen(false)}
              >
                <img className="industry-img scale-20" src={close} />
              </button>
              <div>
                <TemplatesDisplay
                  callback={() => {
                    postData();
                  }}
                />
              </div>
            </div>
          </div>
        )}

        <button
          className="fixed ml-4 mt-4"
          onClick={() => {
            if (activeStep === 0) {
              if (isAuthenticated) {
                navigate("/start");
              } else {
                navigate("/");
              }
            } else {
              setActiveStep(activeStep - 1);
            }
          }}
        >
          <img className="scale-150" src={arrow_back} />
        </button>

        <Box sx={{ width: "100%", pt: 8, px: 10 }}>
          <Stepper activeStep={activeStep}>
            {steps.map((label, index) => (
              <Step key={index}>
                <StepLabel
                  StepIconComponent={(props) => (
                    <CustomStepIcon
                      {...props}
                      label={label}
                      activeColor="#00b8e4" // Active color
                      inactiveColor="#F5F5F5" // Inactive color
                      setActiveStep={setActiveStep}
                      completedSteps={completedSteps}
                      stepIndex={index} // Current step index
                      activeStep={activeStep} // Active step index
                    />
                  )}
                ></StepLabel>
              </Step>
            ))}
          </Stepper>
        </Box>

        <div className="dark-panel py-6 font-code lg:px-10 xl:px-20">
          <div className="ml-auto w-full">{selectedComponent}</div>
        </div>
      </div>
      <Sidebar minimized={true} drawer={false} />
    </div>
  );
}
